/**
 * F2.d.4 - Achievements panel for ProfilePage.
 *
 * Loads GET /me/achievements once, groups by collection, and renders
 * locked / unlocked / legacy tiles with a Dialog for detail + owner-only
 * feature/hide toggles. Ember/navy palette. No coins/confetti. Respects
 * `prefers-reduced-motion`.
 *
 * Secret filter: the backend already excludes hidden secrets from the
 * response and returns hint-only shape for hint-visible ones - we just
 * render what the API sends.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Star, Lock, EyeOff, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const RARITY_ACCENT = {
  Common: 'border-neutral-300 bg-neutral-50/60',
  Uncommon: 'border-success-300 bg-success-50/60',
  Rare: 'border-blue-300 bg-blue-50/60',
  Epic: 'border-purple-300 bg-purple-50/60',
  Legendary: 'border-secondary-500 bg-secondary-50/70',
  Legacy: 'border-amber-500 bg-amber-50/70',
};

const RARITY_TEXT = {
  Common: 'text-neutral-600',
  Uncommon: 'text-success-700',
  Rare: 'text-blue-700',
  Epic: 'text-purple-700',
  Legendary: 'text-secondary-800',
  Legacy: 'text-amber-800',
};

// Collections we hide when the user has zero unlocks in them, so early-
// account visitors aren't overwhelmed with dozens of empty categories
// that depend on infrastructure not yet shipped.
const HIDE_IF_EMPTY = new Set(['referral', 'travel', 'kasai_bakra', 'city']);

const COLLECTION_LABELS = {
  profile_journey: 'Profile Journey',
  self_discovery: 'Self-Discovery',
  attendance: 'Event Attendance',
  participation: 'Event Participation',
  referral: 'Referral Journey',
  hosting: 'Hosting & Contribution',
  reliability: 'Reliability',
  travel: 'Travel Journey',
  kasai_bakra: 'Kasai Bakra',
  founding: 'Founding & Legacy',
  city: 'City Collections',
  seasonal: 'Seasonal',
  event_specific: 'Event Specific',
  secret: 'Secret',
};

const ProgressBar = ({ progress }) => {
  if (!progress || progress.percent == null) return null;
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between text-[10px] text-primary-600 mb-1">
        <span>
          {progress.current ?? 0} of {progress.target ?? '?'}
        </span>
        <span>{progress.percent}%</span>
      </div>
      <Progress
        value={progress.percent || 0}
        aria-label={`${progress.percent}% progress`}
        className="h-1.5 bg-primary-100 [&>div]:bg-gradient-to-r [&>div]:from-ember-500 [&>div]:to-ember-700"
      />
    </div>
  );
};

const Tile = ({ item, onClick }) => {
  const locked = !!item.locked;
  const rarity = item.rarity || 'Common';
  const isLegacy = !!item.legacy && !locked;
  const isSecret = item.collection === 'secret';

  const accent = locked
    ? 'border-primary-100 bg-neutral-50/50 grayscale-[0.7] opacity-70'
    : RARITY_ACCENT[rarity] || RARITY_ACCENT.Common;

  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      data-testid={`achievement-tile-${item.code}`}
      className={`relative text-left rounded-xl border p-4 transition-transform motion-reduce:transform-none hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-secondary-400 ${accent}`}
    >
      {item.featured && !locked && (
        <div
          className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-secondary-100 text-secondary-800 text-[10px] px-2 py-0.5"
          data-testid={`achievement-featured-badge-${item.code}`}
        >
          <Star className="h-3 w-3" /> Featured
        </div>
      )}
      {isLegacy && (
        <div className="absolute top-2 left-2 rounded-full bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5">
          Legacy
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
          {locked ? (
            <Lock className="h-4 w-4 text-primary-500" />
          ) : (
            <Sparkles className="h-4 w-4 text-secondary-700" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-[10px] uppercase tracking-widest ${RARITY_TEXT[rarity] || RARITY_TEXT.Common}`}>
            {rarity}
          </div>
          <div className="font-display text-base font-semibold text-primary-900 truncate">
            {isSecret && locked
              ? (item.hint ? 'Secret · hint below' : 'Locked secret')
              : (item.name || item.code)}
          </div>
          {isSecret && locked && item.hint && (
            <p className="text-xs text-primary-700 italic mt-1 line-clamp-2">
              {item.hint}
            </p>
          )}
          {!(isSecret && locked) && (
            <p className="text-xs text-primary-600 mt-1 line-clamp-2">
              {item.description}
            </p>
          )}
          {locked && <ProgressBar progress={item.progress} />}
        </div>
      </div>
    </button>
  );
};

const DetailModal = ({ open, item, onClose, onToggle }) => {
  if (!item) return null;
  const locked = !!item.locked;
  const canToggle = !locked && item.collection !== 'secret';
  const rarity = item.rarity || 'Common';
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="achievement-detail-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-primary-900">
            {item.name || item.code}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs">
            <span className={`uppercase tracking-widest ${RARITY_TEXT[rarity]}`}>{rarity}</span>
            <span className="text-primary-400">·</span>
            <span className="text-primary-600">{COLLECTION_LABELS[item.collection] || item.collection}</span>
            {item.legacy && (
              <>
                <span className="text-primary-400">·</span>
                <span className="text-amber-800">Legacy</span>
              </>
            )}
          </div>
          {item.description && (
            <p className="text-sm text-primary-800">{item.description}</p>
          )}
          {typeof item.bonus_points === 'number' && (
            <p className="text-xs text-primary-600">
              Bonus points: <span className="font-semibold text-primary-900">{item.bonus_points}</span>
            </p>
          )}
          {item.unlocked_at && (
            <p className="text-xs text-primary-500">
              Unlocked {new Date(item.unlocked_at).toLocaleDateString()}
            </p>
          )}
          {canToggle && (
            <div className="pt-3 border-t border-primary-100 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-primary-900">Feature</div>
                  <div className="text-xs text-primary-500">Show on your public profile (max 3).</div>
                </div>
                <Switch
                  data-testid={`achievement-feature-toggle-${item.code}`}
                  checked={!!item.featured}
                  onCheckedChange={(v) => onToggle(item.code, { featured: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-primary-900">Hide</div>
                  <div className="text-xs text-primary-500">Remove from your collection view.</div>
                </div>
                <Switch
                  data-testid={`achievement-hide-toggle-${item.code}`}
                  checked={!!item.hidden}
                  onCheckedChange={(v) => onToggle(item.code, { hidden: v })}
                />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AchievementsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(null); // { ...item } or null

  const load = async () => {
    try {
      const { data: resp } = await api.get('/me/achievements');
      setData(resp);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const collections = useMemo(() => {
    if (!data) return [];
    return (data.collections || []).filter((c) => {
      if (!HIDE_IF_EMPTY.has(c.code)) return true;
      return c.achievements.some((a) => !a.locked);
    });
  }, [data]);

  const onToggle = async (code, patch) => {
    try {
      await api.patch(`/me/achievements/${code}`, patch);
      await load();
      toast.success(patch.featured != null
        ? (patch.featured ? 'Featured on your profile.' : 'No longer featured.')
        : (patch.hidden ? 'Hidden from your collection.' : 'Shown in your collection.'));
      setDetailOpen((prev) => prev ? { ...prev, ...patch } : prev);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === 'featured_cap_exceeded') {
        toast.error(`Only ${detail.max} can be featured. Unfeature one first.`);
      } else if (e?.response?.status === 429) {
        toast.error('Too many changes. Try again in a moment.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not update.');
      }
    }
  };

  if (loading) {
    return (
      <div data-testid="achievements-panel-loading" className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
      </div>
    );
  }
  if (!data || !collections.length) {
    return (
      <Card data-testid="achievements-panel-empty" className="border shadow-soft">
        <CardContent className="p-8 text-center">
          <Sparkles className="h-6 w-6 mx-auto text-secondary-600 mb-2" />
          <p className="text-sm text-primary-700">
            No achievements yet. RSVP, complete your profile, or add an assessment to start earning.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div data-testid="achievements-panel-root" className="space-y-8">
      <div className="text-sm text-primary-600">
        <span className="font-semibold text-primary-900">{data.total_unlocked}</span> of {data.total_definitions} unlocked
      </div>
      {collections.map((coll) => (
        <section
          key={coll.code}
          data-testid={`achievements-collection-${coll.code}`}
        >
          <h3 className="font-display text-lg font-bold text-primary-900 mb-3">
            {COLLECTION_LABELS[coll.code] || coll.code}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {coll.achievements.map((a) => (
              <Tile key={a.code} item={a} onClick={setDetailOpen} />
            ))}
          </div>
        </section>
      ))}

      <DetailModal
        open={!!detailOpen}
        item={detailOpen}
        onClose={() => setDetailOpen(null)}
        onToggle={onToggle}
      />
    </div>
  );
};

export default AchievementsPanel;
